// C's original spatial theatre. No animation, game or film assets are used.
// Timber pavilions, slate decks and stairs are built as physical instanced parts.
const clamp = value => Math.max(0, Math.min(1, value));
const smooth = (a, b, value) => {const p = clamp((value - a) / (b - a)); return p * p * (3 - 2 * p);};

function surfaceCanvas(kind) {
  const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = kind === 'wood' ? 1024 : 512;
  const ctx = canvas.getContext('2d');
  let seed = 1769; const random = () => (seed = seed * 16807 % 2147483647) / 2147483647;
  if (kind === 'wood') {
    ctx.fillStyle = '#b1a18b'; ctx.fillRect(0, 0, 512, 1024);
    for (let board = 0; board < 8; board++) {
      ctx.fillStyle = `rgba(63,47,30,${.018 + random() * .025})`; ctx.fillRect(board * 64, 0, 64, 1024);
      ctx.fillStyle = 'rgba(35,30,22,.12)'; ctx.fillRect(board * 64, 0, 1.2, 1024);
    }
    for (let i = 0; i < 1100; i++) {
      const x = random() * 512, phase = random() * 6.28, amplitude = random() * 2.0 + .4;
      ctx.strokeStyle = `rgba(49,39,27,${.018 + random() * .045})`; ctx.lineWidth = .3 + random() * .65;
      ctx.beginPath(); for (let y = 0; y <= 1024; y += 40) {const px = x + Math.sin(y * .006 + phase) * amplitude; y ? ctx.lineTo(px, y) : ctx.moveTo(px, y);} ctx.stroke();
    }
  } else {
    const pixels = ctx.createImageData(512, 512);
    for (let y = 0; y < 512; y++) for (let x = 0; x < 512; x++) {
      const grain = (random() - .5) * (kind === 'slate' ? 10 : 5);
      const vein = Math.sin(y * .047 + Math.sin(x * .013) * 1.4) * (kind === 'slate' ? 4 : 1.2);
      const color = kind === 'slate' ? [105,119,113] : [222,215,196], offset = (y * 512 + x) * 4;
      for (let c = 0; c < 3; c++) pixels.data[offset + c] = color[c] + grain + vein;
      pixels.data[offset + 3] = 255;
    }
    ctx.putImageData(pixels, 0, 0);
  }
  return canvas;
}

export function createCWorld({THREE: T, scene, portrait = false}) {
  const root = new T.Group(); root.name = 'C — The floating courts'; scene.add(root);
  const oldBackground = scene.background, oldFog = scene.fog;
  const geometries = new Set(), materials = new Set(), textures = new Set(), modules = [];
  const cube = new T.BoxGeometry(1, 1, 1); geometries.add(cube);
  const texture = kind => {const map = new T.CanvasTexture(surfaceCanvas(kind)); map.colorSpace = T.SRGBColorSpace; map.wrapS = map.wrapT = T.RepeatWrapping; map.anisotropy = 4; textures.add(map); return map;};
  const woodMap = texture('wood'), slateMap = texture('slate'), plasterMap = texture('plaster');
  const material = parameters => {const m = new T.MeshStandardMaterial({roughness: .72, ...parameters}); materials.add(m); return m;};
  const oak = material({map: woodMap, color: '#ddd4c3', roughness: .58});
  const ink = material({color: '#182421', roughness: .67});
  const slate = material({map: slateMap, color: '#6b817a', metalness: .12, roughness: .68});
  const celadon = material({map: plasterMap, color: '#638e7e', roughness: .94});
  const mineral = material({map: plasterMap, color: '#d9cdb2', roughness: .82});
  const bronze = material({color: '#b18a4b', metalness: .72, roughness: .35});
  let totalParts = 0;
  function builder(parent) {
    const batches = new Map();
    function box(size, position, mat = oak, rotation = [0, 0, 0]) {
      if (!batches.has(mat)) batches.set(mat, []); batches.get(mat).push({size, position, rotation}); totalParts++;
    }
    function finish() {
      const dummy = new T.Object3D();
      for (const [mat, parts] of batches) {
        const mesh = new T.InstancedMesh(cube, mat, parts.length); mesh.castShadow = true; mesh.receiveShadow = true;
        parts.forEach((part, i) => {dummy.position.set(...part.position); dummy.rotation.set(...part.rotation); dummy.scale.set(...part.size); dummy.updateMatrix(); mesh.setMatrixAt(i, dummy.matrix);});
        mesh.instanceMatrix.needsUpdate = true; parent.add(mesh);
      }
    }
    return {box, finish};
  }
  function pavilion(parent, width, depth, height, detail = true) {
    const b = builder(parent), x = width / 2, z = depth / 2;
    b.box([width + .5,.34,depth + .5],[0,-.20,0],slate);
    b.box([width,.15,depth],[0,.045,0],oak);
    for (const side of [-1, 1]) {
      for (const end of [-1, 1]) {
        b.box([.24,height,.24],[side*x,height/2,end*z],ink);
        b.box([.08,.46,.28],[side*x-.02,height-.45,end*z],bronze);
      }
      b.box([.26,.38,depth+.72],[side*x,height,0],oak);
      b.box([width+.72,.38,.26],[0,height,side*z],oak);
      b.box([width+.45,.055,.055],[0,height-.28,side*z],bronze);
      // Railings have a fine timber top and sparse black uprights.
      b.box([.075,.075,depth],[side*x,.97,0],oak);
      for (let rail = 0; rail < 6; rail++) b.box([.035,.9,.035],[side*x,.48,-z + rail*depth/5],ink);
    }
    for (let rafter = 0; rafter < 7; rafter++) b.box([.12,.17,depth+.82],[-x+rafter*width/6,height+.26,0],oak);
    b.box([width+.8,.16,depth*.28],[0,height+.40,-depth*.36],slate);
    if (detail) {
      // Side screens leave an open sightline through the pavilion.
      for (let slat = 0; slat < 15; slat++) b.box([.045,height*.68,.085],[-x+.13+slat*.13,height*.48,-z+.045],oak);
      b.box([width*.33,height*.62,.10],[width*.29,height*.37,-z],celadon);
      b.box([width*.30,.10,.65],[-width*.22,.40,-depth*.28],mineral);
    }
    b.finish();
  }

  // One inhabited courtyard is the final destination; the surrounding courts
  // deliberately obey several different gravitational directions.
  const court = new T.Group(); root.add(court);
  const main = builder(court);
  main.box([10.3,.48,15.4],[0,-.34,-.5],slate);
  main.box([9.8,.12,15],[0,-.045,-.5],oak);
  main.box([9.9,.035,.035],[0,.03,7.01],bronze);
  for (const x of [-4.7, 4.7]) {
    for (const z of [-7.5,7]) main.box([.30,6.0,.30],[x,3,z],ink);
    main.box([.34,.40,15.8],[x,6.02,-.25],oak);
  }
  for (const z of [-7.5,7]) {main.box([10.2,.46,.32],[0,6.02,z],oak);main.box([10.2,.055,.055],[0,5.73,z],bronze);}
  for (let i=0;i<9;i++) main.box([.14,.22,6.0],[-4.5+i*1.125,6.35,-4.85],oak);
  // A broken roof, two mineral planes and an open pool make a real courtyard.
  main.box([2.15,.20,15.4],[-3.85,6.52,-.35],slate);
  main.box([2.15,.20,15.4],[3.85,6.52,-.35],slate);
  main.box([.22,4.8,8.5],[-4.8,2.4,-3.3],celadon);
  main.box([4.5,4.8,.22],[-2.5,2.4,-7.6],mineral);
  for(let i=0;i<23;i++) main.box([.075,4.0,.14],[.3+i*.19,2.15,-7.47],oak);
  main.box([3.4,.12,.85],[2.6,.55,-6.75],oak);main.box([3.0,.45,.58],[2.6,.26,-6.75],ink);
  main.box([3.5,.10,5.7],[-1.5,.035,-2.7],slate);
  main.box([.10,.15,5.7],[-3.22,.09,-2.7],bronze);
  main.box([.10,.15,5.7],[.22,.09,-2.7],bronze);
  main.finish();
  const waterMaterial = new T.MeshPhysicalMaterial({color:'#21473e',roughness:.16,metalness:.55,clearcoat:.7}); materials.add(waterMaterial);
  const waterGeometry = new T.PlaneGeometry(3.25,5.45); geometries.add(waterGeometry);
  const water = new T.Mesh(waterGeometry,waterMaterial);water.rotation.x=-Math.PI/2;water.position.set(-1.5,.105,-2.7);court.add(water);
  const stepping = builder(court);
  for(let i=0;i<5;i++) stepping.box([.83,.12,.66],[-1.8+(i%2)*.38,.19,-.55-i*.88],mineral);
  stepping.finish();

  const placements = [
    [-14,-4,2,.30,.0,.0],[-10,4,-13,-.20,.0,.14],[2,9,-14,.38,.12,0],
    [13,5,-9,-.60,0,-.12],[15,-3,7,.65,0,.16],[4,12,11,-.30,.12,.0],
    [-13,12,10,.35,0,-.22],[-3,-9,-11,.80,0,.12],[9,-8,-4,-.15,.0,.0],
    [-17,2,-7,.30,Math.PI/2,.12],[11,13,2,-.36,.0,Math.PI/2]
  ];
  const activePlacements = portrait ? [1,2,3,5,6,7,8,9,10].map(index => placements[index]) : placements;
  activePlacements.forEach(([x,y,z,ry,rx,rz],index)=>{
    const group=new T.Group();group.position.set(x,y,z);group.rotation.set(rx,ry,rz);root.add(group);
    pavilion(group,index%3===0?7:5.4,index%2===0?8:6.2,3.7,index<(portrait?4:8));
    modules.push({group,base:[rx,ry,rz],index});
  });
  // Stairs are flights with actual individual treads, side stringers and rails.
  function stair(position,rotation,count=17) {
    const group=new T.Group();group.position.set(...position);group.rotation.set(...rotation);root.add(group);
    const b=builder(group),rise=.185,run=.32,length=Math.hypot(count*rise,count*run),pitch=Math.atan2(count*rise,count*run);
    for(let i=0;i<count;i++)b.box([2.0,.10,.40],[0,i*rise,i*run],oak);
    for(const x of [-.96,.96]) {
      b.box([.075,.24,length],[x,(count-1)*rise/2-.15,(count-1)*run/2],ink,[-pitch,0,0]);
      b.box([.055,.055,length],[x,(count-1)*rise/2+.98,(count-1)*run/2],bronze,[-pitch,0,0]);
      for(let i=0;i<count;i+=4)b.box([.035,.9,.035],[x,i*rise+.45,i*run],ink);
    }
    b.finish();
    modules.push({group,base:rotation.slice(),index:modules.length,stair:true});
  }
  stair([-9,-1,1],[0,-.45,0]);stair([7.6,3.8,-8],[0,.9,.12]);stair([-5.7,7.3,-15],[0,-.6,.45]);stair([13,-5,8],[.2,.5,-Math.PI/2]);
  // Suspended bridges connect the courts at deliberately incompatible levels.
  for(let index=0;index<(portrait?5:7);index++) {
    const group=new T.Group(),angle=index*.91+.3;
    group.position.set(Math.sin(angle)*12,index%3*4.3-3,Math.cos(angle)*12-1);
    group.rotation.set(index===3?Math.PI/2:0,-angle,index%2?.12:-.12);root.add(group);
    const b=builder(group);b.box([2.0,.22,10],[0,0,0],slate);b.box([1.9,.08,10],[0,.14,0],oak);
    for(const x of [-.94,.94]){b.box([.055,.055,10],[x,1.02,0],bronze);for(let i=0;i<10;i++)b.box([.035,.86,.035],[x,.57,-4.8+i*1.06],ink);}b.finish();
  }
  // A few distant, reduced-detail courts extend the vertical parallax.
  for(let index=0;index<(portrait?3:7);index++) {
    const group=new T.Group(),angle=index*.95;group.position.set(Math.sin(angle)*29,(index%4-1.5)*9,Math.cos(angle)*29-6);group.rotation.set(index%2?.18:-.22,-angle,index===4?Math.PI/2:0);root.add(group);pavilion(group,8,10,4.2,false);
  }

  const sun=new T.DirectionalLight('#ffe0a3',4.2);sun.position.set(-18,30,14);sun.target.position.set(0,2,-2);sun.castShadow=true;
  sun.shadow.mapSize.set(1024,1024);Object.assign(sun.shadow.camera,{left:-36,right:36,top:40,bottom:-36,near:1,far:95});sun.shadow.bias=-.00010;sun.shadow.normalBias=.035;
  const sky=new T.HemisphereLight('#b7d7ca','#1b2c26',1.05),bounce=new T.DirectionalLight('#ffcf8c',.9);bounce.position.set(10,4,-10);
  scene.add(sun,sun.target,sky,bounce);
  const background=new T.Color('#0e1b18'),fog=new T.FogExp2(background,.017);scene.background=background;scene.fog=fog;
  let time=0;
  function update(seconds) {
    time=seconds;const fold=smooth(.75,3.35,time)*(1-smooth(4.45,6.05,time));
    for(const item of modules){const {group,base,index}=item;group.rotation.set(base[0]+Math.sin(index*1.9)*fold*(item.stair?.34:.70),base[1]+fold*(index%2?.24:-.24),base[2]+Math.cos(index*1.2)*fold*(item.stair?.18:.55));}
    sun.intensity=4.2+.6*smooth(3.6,5.8,time);sky.intensity=.84+.21*smooth(1.0,3.8,time);
  }
  return {update,inspect:()=>({name:'C — Floating architectural courts',parts:totalParts,pavilions:portrait?13:19,stairs:4,bridges:portrait?5:7,foldDegrees:Math.round(smooth(.75,3.35,time)*(1-smooth(4.45,6.05,time))*40),time}),dispose(){scene.remove(root,sun,sun.target,sky,bounce);root.traverse(object=>{if(object.isInstancedMesh)object.dispose();});sun.shadow.dispose();for(const g of geometries)g.dispose();for(const m of materials)m.dispose();for(const t of textures)t.dispose();scene.background=oldBackground;scene.fog=oldFog;}};
}
